import { Suspense, useRef, useEffect, useCallback } from "react"
import { Canvas, useThree, useFrame } from "@react-three/fiber"
import styled from "styled-components"
import { Spherical, MathUtils, Vector3 } from "three"

import StaticGeometry, { SemiStaticGeometry } from "./Loaders/StaticGeometry"
import InteractiveBuilding from "./Loaders/InteractiveBuilding"
import FilteringUI from "./UI/FilteringUI"
import CoordinatesBroadcaster from "../../state/CoordinatesBroadcaster"
import CameraControls from "./Components/CameraControls"
import ApartmentCard from "./Components/ApartmentCard"
import InstanceGeometry from "./Loaders/InstanceGeometry"
import PoiMarkers from "./Loaders/PoiMarkers"
import FavouriteUI from "./UI/FavouriteUI"
import POI from "./Components/POI"
import CloudsLoader from "./Loaders/CloudsLoader"
import { useStore } from "../../state/store"
// import { isIOS } from "../Utils/Helpers"

const Context = () => {
  const { gl } = useThree()

  useEffect(() => {
    gl.getContext().canvas.addEventListener(
      "webglcontextlost",
      function (event) {
        event.preventDefault()
        setTimeout(() => gl.forceContextRestore(), 1)
      },
      false
    )
    gl.getContext().canvas.addEventListener("webglcontextrestored", function (event) {}, false)
  }, [])

  return null
}

function Loader() {
  const setLoaded = useStore(state => state.setLoaded)
  const setCurrentCameraPositionID = useStore(state => state.setCurrentCameraPositionID)
  useEffect(() => {
    setCurrentCameraPositionID("init")
    // console.log("loading")
    return () => {
      setLoaded(true)
    }
  }, [])
  return null
}

function Compass({ test }) {
  var dir = new Vector3()
  var sph = new Spherical()

  useFrame(({ camera }) => {
    camera.getWorldDirection(dir)
    sph.setFromVector3(dir)
    test(sph.setFromVector3(dir))
  })

  return null
}

const DisableRender = () => useFrame(() => null, 1000)

export default function ApartmentFinder({
  staticGeometryID = "st",
  semiStaticGeometryID = "ss",
  interactiveBuildingID = "ek"
}) {
  const visible = useStore(state => state.visible)
  const apartments = useStore(state => state.apartments)

  const compassRef = useRef()

  const test = useCallback(v => {
    if (compassRef.current)
      compassRef.current.style.transform = `rotate(${MathUtils.radToDeg(v.theta) - 240}deg)`
  })

  return (
    <Holder visible={visible}>
      {apartments && <ApartmentCard />}
      {apartments && <FavouriteUI />}
      {apartments && process.env.REACT_APP_ROLE !== "recipient" && <FilteringUI />}
      {apartments && <POI />}
      <img
        ref={compassRef}
        style={{
          // background: "white",
          // borderRadius: "100%",
          position: "absolute",
          bottom: 0,
          left: 0,
          margin: "1.5%",
          pointerEvents: "initial",
          right: 0,
          zIndex: 3000000
        }}
        src={`${process.env.PUBLIC_URL}/assets/apartmentfinder/icons/compass.svg`}
        width={60}
        height={60}
        alt=""
        title="Menu"
      ></img>

      <Canvas
        shadows
        gl={{
          antialias: true,
          sortObjects: false
        }}
        camera={{
          position: [0, 10000, 0],
          fov: 60,
          near: 300,
          far: 150000
        }}
      >
        <Context />

        {!visible && <DisableRender />}

        <Suspense fallback={<Loader />}>
          {/* <Glass /> */}
          <CloudsLoader />
          <pointLight position={[10, 10000, 10]} intensity={0.8} color={"white"} />
          <pointLight position={[100000, 10000, 10000]} intensity={0.5} color={"white"} />
          <ambientLight intensity={0.6} />
          <PoiMarkers />
          <InstanceGeometry />
          <StaticGeometry geometryID={staticGeometryID} />
          <SemiStaticGeometry geometryID={semiStaticGeometryID} />
          {apartments && <InteractiveBuilding buildingID={interactiveBuildingID} />}

          <Compass test={test} />
        </Suspense>

        <CameraControls />
        {process.env.REACT_APP_MARKETING_SUITE && <CoordinatesBroadcaster />}
      </Canvas>
    </Holder>
  )
}

const Holder = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 0;
  height: 100%;
  width: 100%;
  background-color: #8eb1c9;
  user-select: none;
  visibility: ${props => (props.visible ? "visible" : "hidden")};
`
